import styled, { css } from 'styled-components';

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

interface ModalContainerProps {
  size?: 'small' | 'medium' | 'large' | 'full';
}

export const ModalContainer = styled.div<ModalContainerProps>`
  background-color: ${({ theme }) => theme.white};
  border-radius: 8px;
  box-shadow: 0 0 12px ${({ theme }) => theme.white === '#1a1a1a' ? 'rgba(255, 255, 255, 0.25)' : theme.darkColor};
  width: 90%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideIn 0.2s ease-in-out;

  @keyframes slideIn {
    from {
      transform: translateY(-20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  ${({ size }) => {
    switch (size) {
      case 'small':
        return css`
          max-width: 500px;
        `;
      case 'medium':
        return css`
          max-width: 800px;
        `;
      case 'large':
        return css`
          max-width: 1200px;
        `;
      case 'full':
        return css`
          max-width: 95%;
          width: 95%;
          height: 95vh;
        `;
      default:
        return css`
          max-width: 800px;
        `;
    }
  }}
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.lightGray};
  background-color: ${({ theme }) => theme.lightColor};
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.primaryColor};
  display: flex;
  align-items: center;

  svg {
    margin-right: 10px;
    color: ${({ theme }) => theme.primaryColor};
  }
`;

export const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background-color: ${({ theme }) => theme.lightGrayColor};
  }

  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.darkGray};
  }
`;

export const ModalBody = styled.div`
  padding: 16px 24px;
  overflow-y: auto;
  flex: 1;
  max-height: calc(90vh - 130px);
  background-color: ${({ theme }) => theme.white};
  color: ${({ theme }) => theme.textColor};
`;

export const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => theme.lightGray};
  gap: 10px;
  background-color: ${({ theme }) => theme.lightColor};
`;

export const Button = styled.button`
  height: 40px;
  background-color: ${({ theme }) => theme.primaryColor};
  color: ${({ theme }) => theme.white};
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  min-width: 100px;

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.white};
    margin-right: 8px;
  }

  &:hover {
    background-color: ${({ theme }) => theme.primaryColorHover};
    transform: translateY(-2px);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    background-color: ${({ theme }) => theme.lightGray};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

export const SecondaryButton = styled(Button)`
  background-color: ${({ theme }) => theme.lightGrayColor};
  color: ${({ theme }) => theme.darkColor};
  border: 1px solid ${({ theme }) => theme.borderColor};

  &:hover {
    background-color: ${({ theme }) => theme.lightGrayHover};
    color: ${({ theme }) => theme.darkColor};
  }

  svg {
    color: ${({ theme }) => theme.darkColor};
  }
`;
